import { AsyncPipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	DestroyRef,
	ElementRef,
	EventEmitter,
	Inject,
	input,
	OnChanges,
	OnDestroy,
	Optional,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { Activity } from '@go-modules/interfaces/activity';
import type { Task, TaskInstructionDraft } from '@go-modules/interfaces/task';
import { TaskType } from '@go-modules/interfaces/task';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import {
	NgxActivityViewComponent
} from '@go-modules/components/activity-view/activity-view.component';
import { BehaviorSubject } from 'rxjs';
import {
	SelectedService,
	selectedServiceToken
} from 'go-modules/services/selected/selected.service';
import { ActivityEditModeService } from '@go-modules/services/activity-edit-mode/activity-edit-mode.service';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import {
	TaskInstructionsDialogComponent,
	type TaskInstructionsDialogData
} from '@go-modules/components/dialogs/task-instructions-dialog/task-instructions-dialog.component';
import type { LibraryDialogContext } from '@go-modules/components/dialogs/library-dialog/library-dialog.component';
import type { MediaSchema } from 'go-modules/models/media';
import { ResponsiveLayoutService } from '@go-modules/services/responsive-layout/responsive-layout.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NgxTaskService } from '@go-modules/services/task/task.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
	ACTIVITY_EDITOR_CONTEXT,
	ActivityEditorContext
} from '@go-modules/components/dialogs/activity-preview-editor/activity-editor-context';
import { TaskListNavigatorComponent } from '@go-modules/components/activity-task-sessions/task-list-navigator/task-list-navigator.component';
import { TaskInstructionComponent } from '@go-modules/components/activity-task-sessions/task-details/instructions-panel/task-instruction.component';
import { SanitizePipeModule } from '@go-modules/pipes/sanitize/sanitize.pipe.module';
import { MediaCardModule } from '@go-modules/components/media-card/media-card.module';
import { DisabledInteractiveDirective } from '@go-modules/directives/disabled-interactive/disabled-interactive.directive';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';
import {
	ActivityPreviewInlineTaskInstructionStateService
} from '@go-modules/components/dialogs/activity-preview-editor/activity-preview-inline-task-instruction-state.service';

interface GroupContext {
	group_id?: number | null;
}

type TaskInstructionUpdate = Pick<TaskInstructionDraft, 'text'> & { media: Partial<MediaSchema> | null };

@Component({
	selector: 'task-details',
	templateUrl: './task-details.component.html',
	styleUrls: ['./task-details.component.scss'],
	standalone: true,
	imports: [
		AsyncPipe,
		TranslateModule,
		TaskListNavigatorComponent,
		TaskInstructionComponent,
		SanitizePipeModule,
		MediaCardModule,
		DisabledInteractiveDirective
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskDetailsComponent implements OnChanges, OnDestroy {
	public task = input.required<Task>();
	public totalTasks = input<number>(0);
	public currentTaskIndex = input<number>(0);
	public previousButtonLabelKey = input('task-details_nav-back');

	public isMobile = toSignal(this.responsiveLayoutService.isMobileSize$, { initialValue: false });

	@Output() public navigateBack = new EventEmitter<void>();
	@Output() public navigateForward = new EventEmitter<void>();
	@Output() public openNavigator = new EventEmitter<void>();

	@ViewChild('instructionsContent') public set instructionsContentSetter (el: ElementRef<HTMLDivElement> | undefined) {
		this.instructionsContent = el;
		if (el) {
			this.scheduleToggleEvaluation();
		}
	}
	private instructionsContent?: ElementRef<HTMLDivElement>;

	public expanded$ = new BehaviorSubject<boolean>(false);
	public showToggle$ = new BehaviorSubject<boolean>(false);
	public isAddingInstructionForCurrentTask = computed(() =>
		this.inlineTaskInstructionState?.isAddingTaskInstruction(this.currentTask()) ?? false
	);
	private toggleEvaluationTimeout: ReturnType<typeof setTimeout> | null = null;

	public currentTask = computed(() => {
		if (this.editorContext != null) {
			return this.editorContext.selectedTask() ?? this.task();
		}

		const taskId = this.task().id;
		return this.activityEditModeService.tasksById().get(taskId) ?? this.task();
	});

	public customInstructionsHtml = computed(() => {
		const task = this.currentTask();
		const instruction = task?.instruction;
		return instruction?.text ? this.formatContent(instruction.text, true) : '';
	});

	public titleHtml = computed(() => {
		const activity = this.getCurrentActivity();
		const task = this.currentTask();
		const titleAndDescription = activity && task
			? this.ngxActivityService.getTaskTitleAndDescription(activity, task)
			: { title: '', description: '' };
		return this.formatContent(titleAndDescription.title);
	});

	public displayTitleHtml = computed(() => {
		return this.titleHtml();
	});

	public toolInstructionsHtml = computed(() => {
		const activity = this.getCurrentActivity();
		const task = this.currentTask();
		const titleAndDescription = activity && task
			? this.ngxActivityService.getTaskTitleAndDescription(activity, task)
			: { title: '', description: '' };
		return this.formatContent(titleAndDescription.description, true);
	});

	private hasInstructionMedia = computed(() => {
		const task = this.currentTask();
		return !!task?.instruction?.media;
	});

	constructor (
		private cdr: ChangeDetectorRef,
		private destroyRef: DestroyRef,
		private elementRef: ElementRef<HTMLElement>,
		private ngxActivityService: NgxActivityService,
		public activityEditModeService: ActivityEditModeService,
		private routerService: RouterService,
		private ngxGoModalService: GoModalService,
		private responsiveLayoutService: ResponsiveLayoutService,
		private taskService: NgxTaskService,
		private translate: TranslateService,
		private featureFlag: NgxFeatureFlagService,
		@Optional() @Inject(ACTIVITY_EDITOR_CONTEXT) private editorContext: ActivityEditorContext | null,
		@Optional() private inlineTaskInstructionState: ActivityPreviewInlineTaskInstructionStateService | null,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Optional() private activityViewComponent?: NgxActivityViewComponent
	) {}

	public ngOnChanges (changes: SimpleChanges): void {
		if (changes.task || changes.activity) {
			this.expanded$.next(false);
			this.scheduleToggleEvaluation();
		}
	}

	public ngOnDestroy (): void {
		this.clearToggleEvaluationTimeout();
	}

	public toggleEditMode (): void {
		if (this.editorContext != null) {
			return;
		}

		if (this.activityViewComponent) {
			this.activityViewComponent.toggleEditMode();
		} else {
			// Fallback
			const currentParams = this.routerService.getCurrentParams();
			const currentQueryParams = this.routerService.getCurrentQueryParams();
			const currentRoute = this.routerService.getCurrentRouteName();

			this.routerService.navigateTo(currentRoute as ROUTE_NAMES, currentParams, {
				queryParams: {...currentQueryParams, mode: 'edit'}
			});
		}
	}

	public onCancelEdit (): void {
		if (this.isSaving()) {
			return;
		}

		if (this.editorContext != null) {
			this.editorContext.requestCancel();
			return;
		}

		if (this.activityEditModeService.hasUnsavedChanges()) {
			this.activityEditModeService.confirmDiscardChanges().subscribe((confirmed) => {
				if (confirmed) {
					this.activityEditModeService.revertChanges();
					this.exitEditMode();
				}
			});
		} else {
			// No changes, just exit edit mode
			this.activityEditModeService.exitEditMode();
			this.exitEditMode();
		}
	}

	private exitEditMode (): void {
		const currentParams = this.routerService.getCurrentParams();
		const currentQueryParams = this.routerService.getCurrentQueryParams();
		const currentRoute = this.routerService.getCurrentRouteName();

		// Remove mode from query params
		const remainingParams = { ...currentQueryParams };
		delete remainingParams.mode;
		this.routerService.navigateTo(currentRoute as ROUTE_NAMES, currentParams, {
			queryParams: remainingParams
		});
	}

	public onSaveEdit (): void {
		if (this.isSaving()) {
			return;
		}

		if (this.editorContext != null) {
			this.editorContext.requestSaveAndClose();
			return;
		}

		this.activityViewComponent?.toggleEditMode();
	}

	public isInEditMode (): boolean {
		return this.editorContext?.isEditMode() ?? this.activityEditModeService.isEditMode();
	}

	public isSaving (): boolean {
		return this.editorContext?.isSaving() ?? this.activityEditModeService.isSaving();
	}

	public shouldShowInlineEditActions (): boolean {
		return this.editorContext == null;
	}

	public hasInstructorRole (): boolean {
		if (this.editorContext != null) {
			return true;
		}

		const group = this.selectedService.getGroup();
		return group?.hasInstructorRole ? group.hasInstructorRole(true) : false;
	}

	public toggleExpanded (): void {
		this.expanded$.next(!this.expanded$.value);
		this.scheduleToggleEvaluation();
	}

	public shouldShowCustomInstructions (): boolean {
		const currentTask = this.currentTask();
		const activity = this.getCurrentActivity();
		if (activity?.has_single_recording_attempt && !this.hasInstructorRole()) {
			return false;
		}
		if (this.shouldUseInlineTaskInstructions() && this.isAddingInstructionForCurrentTask()) {
			return true;
		}
		return !!currentTask?.instruction?.text || !!currentTask?.instruction?.media;
	}

	public shouldShowPrivateSubheader (): boolean {
		const activity = this.getCurrentActivity();
		const task = this.currentTask();

		if (!task || !activity) {
			return false;
		}

		if (this.editorContext != null) {
			return task.type === TaskType.Present &&
				this.editorContext.tasks().some((currentTask) => currentTask.type === TaskType.Submit) &&
				!this.isMobile();
		}

		return task.type === TaskType.Present &&
			this.taskService.hasSubmitTask(activity.activity_id) &&
			!this.isMobile();
	}

	public shouldShowMedia (): boolean {
		return this.hasInstructionMedia() && (this.editorContext != null || this.expanded$.value);
	}

	public isComplete (): boolean {
		return this.currentTask()?.completed ?? false;
	}

	public openAddTaskInstructionModal (event?: MouseEvent) {
		if (this.shouldUseInlineTaskInstructions()) {
			this.openInlineInstructionForTask(this.currentTask());
			this.cdr.markForCheck();
			return;
		}

		const task = this.currentTask();
		const instruction = task.instruction;
		const focusReturnTarget = event?.currentTarget instanceof HTMLElement
			? event.currentTarget
			: null;
		const fallbackFocusTarget = this.elementRef.nativeElement.querySelector<HTMLElement>('.task-details-title');

		return this.ngxGoModalService.open(TaskInstructionsDialogComponent, true, {
			data: {
				text: instruction?.text ?? '',
				media: instruction?.media ?? null,
				...this.getTaskInstructionsDialogContextData()
			} as TaskInstructionsDialogData
		}).afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (!result) {
					this.restoreFocusAfterInstructionDialog(focusReturnTarget, fallbackFocusTarget);
					return;
				}

				const instruction: TaskInstructionDraft = {
					text: result.text,
					media: result.media
				};

				const editedTask = { ...task, instruction };
				this.updateTask(editedTask);
				this.restoreFocusAfterInstructionDialog(
					this.editorContext == null ? null : focusReturnTarget,
					fallbackFocusTarget
				);
				this.cdr.markForCheck();
			});
	}

	public handleEditInstructions (e: TaskInstructionUpdate) {
		const task = this.currentTask();
		if (this.shouldUseInlineTaskInstructions()) {
			this.openInlineInstructionForTask(task);
		}

		if (e.text === '' && e.media === null) {
			e = null;
		}

		this.updateTask({
			...task,
			instruction: e
		});
	}

	public handleRemoveInstruction () {
		const task = this.currentTask();
		this.closeInlineInstructionForTask(task);
		this.updateTask({
			...task,
			instruction: null
		});
		this.showToggle$.next(false);
		this.expanded$.next(false);
	}

	public shouldUseInlineTaskInstructions (): boolean {
		return this.editorContext != null &&
			this.inlineTaskInstructionState != null &&
			this.isNewActivityPreviewEditorDialogEnabled();
	}

	public isNewActivityPreviewEditorDialogEnabled (): boolean {
		return this.featureFlag.isAvailable('ACTIVITY_PREVIEW_EDITOR_DIALOG');
	}

	public shouldShowInlineTaskInstruction (): boolean {
		return this.shouldUseInlineTaskInstructions() && (
			this.isAddingInstructionForCurrentTask() ||
			this.shouldShowCustomInstructions()
		);
	}

	public getModifierSubheader (): string | null {
		const activity = this.getCurrentActivity();

		if (activity?.has_response_media) {
			if (activity.live_session_enabled && !activity.has_single_recording_attempt) {
				return this.translate.instant('common_live');
			}

			if (activity.has_single_recording_attempt) {
				return this.translate.instant('common_single-attempt');
			}
		}

		return null;
	}

	private openInlineInstructionForTask (task: Task): void {
		this.inlineTaskInstructionState?.openTaskInstruction(task);
	}

	private closeInlineInstructionForTask (task: Task): void {
		this.inlineTaskInstructionState?.closeTaskInstruction(task);
	}

	private formatContent (value: string, convertNewLines: boolean = false): string {
		if (!value) {
			return '';
		}

		let workingValue = value;
		const hasHtml = /<[^>]+>/i.test(workingValue);

		if (!hasHtml && convertNewLines) {
			workingValue = workingValue.replace(/\r?\n|\r|\n/g, '<br/>');
		}

		return workingValue;
	}

	private scheduleToggleEvaluation (): void {
		this.clearToggleEvaluationTimeout();
		this.toggleEvaluationTimeout = setTimeout(() => {
			this.toggleEvaluationTimeout = null;
			this.evaluateToggleVisibility();
		});
	}

	private clearToggleEvaluationTimeout (): void {
		if (this.toggleEvaluationTimeout == null) {
			return;
		}

		clearTimeout(this.toggleEvaluationTimeout);
		this.toggleEvaluationTimeout = null;
	}

	private evaluateToggleVisibility (): void {
		const element = this.instructionsContent?.nativeElement;

		const activity = this.getCurrentActivity();
		if (activity?.has_single_recording_attempt && !this.hasInstructorRole()) {
			this.showToggle$.next(false);
			this.cdr.markForCheck();
			return;
		}

		if (!element) {
			this.showToggle$.next(this.hasInstructionMedia());
			this.cdr.markForCheck();
			return;
		}

		const fullHeight = this.getFullContentHeight(element);
		const collapsedHeight = this.getCollapsedContentHeight(element);

		const isOverflowing = Math.round(fullHeight) - Math.round(collapsedHeight) > 2;
		this.showToggle$.next(isOverflowing || this.hasInstructionMedia());

		this.cdr.markForCheck();
	}

	private restoreFocusAfterInstructionDialog (
		focusReturnTarget: HTMLElement | null,
		fallbackFocusTarget: HTMLElement | null
	): void {
		if (focusReturnTarget?.isConnected) {
			focusReturnTarget.focus();
			return;
		}

		fallbackFocusTarget?.focus();
	}

	private getFullContentHeight (element: HTMLElement): number {
		const hadCollapsed = element.classList.contains('collapsed');
		if (hadCollapsed) {
			element.classList.remove('collapsed');
		}

		element.classList.add('measure-full');
		const fullHeight = element.scrollHeight;
		element.classList.remove('measure-full');

		if (hadCollapsed) {
			element.classList.add('collapsed');
		}

		return fullHeight;
	}

	private getCollapsedContentHeight (element: HTMLElement): number {
		const hadCollapsed = element.classList.contains('collapsed');
		if (!hadCollapsed) {
			element.classList.add('collapsed');
		}

		const collapsedHeight = element.clientHeight;

		if (!hadCollapsed) {
			element.classList.remove('collapsed');
		}

		return collapsedHeight;
	}

	private getCurrentActivity (): Activity | null {
		if (this.editorContext != null) {
			return this.editorContext.activity();
		}

		return this.activityEditModeService.currentActivity() ?? this.selectedService.getActivity();
	}

	private updateTask (task: Task): void {
		if (this.editorContext != null) {
			this.editorContext.updateTask(task);
			return;
		}

		this.activityEditModeService.updateEditedTask(task);
	}

	private getTaskInstructionsDialogContextData (): Partial<Pick<TaskInstructionsDialogData, 'groupId' | 'context'>> {
		if (this.editorContext == null) {
			return {};
		}

		return {
			groupId: this.resolveEditorContextGroupId(),
			context: this.getLibraryDialogContext()
		};
	}

	private resolveEditorContextGroupId (): number | null {
		const groupId = (this.editorContext?.group?.() as GroupContext | null)?.group_id;
		if (groupId != null) {
			return groupId;
		}

		return this.editorContext?.activity()?.group_id ?? null;
	}

	private getLibraryDialogContext (): LibraryDialogContext {
		return {
			group: this.editorContext?.group?.() ?? null,
			account: this.editorContext?.account?.() ?? null,
			org: this.editorContext?.org?.() ?? null,
			license: this.editorContext?.license?.() ?? null,
			orgSettings: this.editorContext?.orgSettings?.() ?? null
		};
	}
}
